import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import logger from './services/logger';
import { defaultGenomeBrowserPath } from './services/authNavigation';

// Components
import ModernLayout from './components/ModernLayout';
import PageLoader, { PageErrorBoundary } from './components/PageLoader';
import AccountRecovery from './pages/AccountRecovery';

// Pages
import ModernLogin from './pages/ModernLogin';
import ModernRegister from './pages/ModernRegister';
const ModernDashboard = lazy(() => import('./pages/ModernDashboard'));
const BlastSearch = lazy(() => import('./pages/BlastSearch'));
const BlastResults = lazy(() => import('./pages/BlastResults'));
const JBrowseView = lazy(() => import('./pages/JBrowseView'));
const JBrowseBrowser = lazy(() => import('./pages/JBrowseBrowser'));
const JBrowseSyntenyView = lazy(() => import('./pages/JBrowseSyntenyView'));
const DataBrowser = lazy(() => import('./pages/DataBrowser'));
const AdminMode = lazy(() => import('./pages/AdminMode'));
const Integrations = lazy(() => import('./pages/Integrations'));

const userHasCapability = (currentUser, capability) =>
  !capability || Boolean(currentUser?.capabilities?.[capability]);

const userHasCapabilities = (currentUser, capabilities = []) =>
  capabilities.every(capability => userHasCapability(currentUser, capability));

// Protected route component
const ProtectedRoute = ({ children, capability, capabilities = [] }) => {
  const { isAuthenticated, currentUser, loading, authError, retryAuth } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader label="Checking your access…" />;
  }

  if (authError) {
    return (
      <div role="alert" className="card p-6 space-y-4">
        <p>{authError}</p>
        <button type="button" className="btn-primary btn-md" onClick={retryAuth}>
          Retry access check
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (!userHasCapabilities(currentUser, [capability, ...capabilities].filter(Boolean))) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const HomeRoute = () => {
  const { loading, isAuthenticated, authError } = useAuth();
  if (loading) return <PageLoader />;
  return <Navigate to={isAuthenticated || authError ? '/dashboard' : '/jbrowse/public'} replace />;
};

const DefaultBrowserRoute = () => {
  const { loading, isAuthenticated, currentUser, authError, retryAuth } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader label="Checking your access…" />;
  if (authError)
    return (
      <div role="alert" className="card p-6 space-y-4">
        <p>{authError}</p>
        <button type="button" className="btn-primary btn-md" onClick={retryAuth}>
          Retry access check
        </button>
      </div>
    );
  return (
    <Navigate
      to={`${defaultGenomeBrowserPath(isAuthenticated ? currentUser : null)}${location.search}${
        location.hash
      }`}
      replace
    />
  );
};

function App() {
  useEffect(() => {
    logger.info('Application initialized', {
      environment: process.env.NODE_ENV,
      apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    });

    // Log navigation changes
    const handleNavigation = () => {
      if (/^\/(reset-password|verify-email)(?:\/|$)/.test(window.location.pathname)) return;
      logger.info('Navigation', {
        path: window.location.pathname,
      });
    };

    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  return (
    <PageErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<ModernLayout />}>
            <Route index element={<HomeRoute />} />
            <Route path="login" element={<ModernLogin />} />
            <Route path="register" element={<ModernRegister />} />
            <Route path="forgot-password" element={<AccountRecovery mode="request" />} />
            <Route path="reset-password/:uid/:token" element={<AccountRecovery mode="reset" />} />
            <Route path="verify-email/:token" element={<AccountRecovery mode="verify" />} />
            <Route path="verify-email" element={<AccountRecovery mode="resend" />} />
            <Route path="dashboard" element={<ModernDashboard />} />
            <Route path="data" element={<DataBrowser />} />
            <Route
              path="integrations"
              element={
                <ProtectedRoute>
                  <Integrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="upload"
              element={
                <ProtectedRoute capability="can_modify_lab_data">
                  <Navigate to="/data?upload=dataset" replace />
                </ProtectedRoute>
              }
            />
            <Route path="blast" element={<BlastSearch />} />
            <Route path="blast/results/guest" element={<BlastResults />} />
            <Route
              path="blast/results/:id"
              element={
                <ProtectedRoute>
                  <BlastResults />
                </ProtectedRoute>
              }
            />
            <Route path="jbrowse" element={<DefaultBrowserRoute />} />
            <Route path="jbrowse/public" element={<JBrowseBrowser visibilityScope="public" />} />
            <Route
              path="jbrowse/private"
              element={
                <ProtectedRoute capability="can_view_lab_data">
                  <JBrowseBrowser visibilityScope="private" />
                </ProtectedRoute>
              }
            />
            <Route
              path="jbrowse/new-assembly"
              element={
                <ProtectedRoute capability="can_modify_lab_data">
                  <Navigate to="/data?upload=assembly" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin"
              element={
                <ProtectedRoute capability="can_manage_roles">
                  <AdminMode />
                </ProtectedRoute>
              }
            />
            <Route path="jbrowse/:datasetId" element={<JBrowseView />} />
            <Route
              path="jbrowse/blast/:blastResultId"
              element={
                <ProtectedRoute>
                  <JBrowseView />
                </ProtectedRoute>
              }
            />
            <Route path="jbrowse/synteny/:syntenyMapId" element={<JBrowseSyntenyView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </PageErrorBoundary>
  );
}

export default App;
